import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight, ClipboardList, Truck, Armchair, Ruler, Headphones, Plus, Minus } from "lucide-react";

const services = [
  {
    id: "procurement",
    title: "Procurement Solutions",
    tag: "01",
    icon: ClipboardList,
    body:
      "Structured sourcing, vendor evaluation and contract management for institutions, enterprises and public buyers.",
    deliverables: ["Vendor discovery & evaluation", "Comparative techno-commercial analysis", "Compliance documentation"],
    scope: "Institutional, corporate and public-sector procurement — from single-item sourcing to multi-year framework contracts.",
    expertise: ["GeM & tender bidding", "Contract negotiation", "Multi-vendor coordination"],
  },
  {
    id: "supply",
    title: "Supply Solutions",
    tag: "02",
    icon: Truck,
    body:
      "Reliable multi-category supply with logistics coordination, quality checks and delivery discipline.",
    deliverables: ["End-to-end order fulfilment", "Warehousing & last-mile logistics", "QA/QC on despatch"],
    scope: "Recurring and one-time supply programmes across furniture, equipment, consumables and technical goods.",
    expertise: ["Multi-location distribution", "Cold-chain & fragile handling", "Documentation & MIS reporting"],
  },
  {
    id: "interiors",
    title: "Interior Design",
    tag: "03",
    icon: Armchair,
    body:
      "Considered interiors for offices, institutions and hospitality — from spatial planning to fit-out and handover.",
    deliverables: ["Concept, moodboards & 3D visualisation", "Material & finish specifications", "Turnkey fit-out & snagging"],
    scope: "Offices, showrooms, institutional spaces, hospitality and residential — from 500 sq. ft. cabins to full-floor builds.",
    expertise: ["Spatial planning", "MEP coordination", "Furniture & millwork detailing"],
  },
  {
    id: "execution",
    title: "Project Execution",
    tag: "04",
    icon: Ruler,
    body:
      "Turnkey execution with structured programme management, on-site supervision and quality reporting.",
    deliverables: ["Programme & schedule management", "On-site supervision", "Weekly progress & quality reporting"],
    scope: "Turnkey delivery of interior, supply and infrastructure projects — with defined milestones and accountability.",
    expertise: ["Vendor orchestration", "Risk & timeline management", "Handover documentation"],
  },
  {
    id: "support",
    title: "Business Support Solutions",
    tag: "05",
    icon: Headphones,
    body:
      "Operational and administrative capability — from tender assistance to vendor liaison and documentation.",
    deliverables: ["Tender & bid documentation", "Vendor liaison & follow-through", "Back-office & operations support"],
    scope: "Continuing engagements for organisations that need reliable execution capacity without the overhead.",
    expertise: ["GeM/tender assistance", "Compliance filings", "Ongoing business operations"],
  },
];

export default function Services() {
  const [open, setOpen] = useState(null);

  return (
    <section
      id="services"
      data-testid="services-section"
      className="relative bg-[var(--nv-navy)] text-[var(--nv-paper)] py-28 md:py-40 nv-grain overflow-hidden"
    >
      {/* Blueprint decorative layer — carries the Hero language into a dark context */}
      <ServicesBlueprintLayer />

      <div className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-10">
        <div className="grid grid-cols-12 gap-6 items-end mb-16 md:mb-24">
          <div className="col-span-12 md:col-span-5">
            <div className="overline text-[var(--nv-gold)] flex items-center gap-3" data-testid="services-overline">
              <span className="inline-block w-6 h-px bg-[var(--nv-gold)]" />
              <span>Services — Five Disciplines</span>
            </div>
            <h2 className="mt-6 display text-5xl md:text-7xl" data-testid="services-heading">
              What we <span className="italic text-[var(--nv-gold)]">do.</span>
            </h2>
          </div>
          <p className="col-span-12 md:col-span-5 md:col-start-8 text-[15px] leading-relaxed text-[var(--nv-paper)]/75">
            Five integrated service disciplines delivered through one accountable partner —
            combining technical expertise, reliable execution, and long-term business support
            under a single trusted relationship.
          </p>
        </div>

        <ul className="border-y border-[var(--nv-line-dark)]" data-testid="services-list">
          {services.map((s, i) => {
            const Icon = s.icon;
            const isOpen = open === s.id;
            return (
              <li
                key={s.id}
                className="group relative border-b border-[var(--nv-line-dark)] last:border-b-0"
                data-testid={`service-item-${s.id}`}
              >
                <motion.button
                  type="button"
                  onClick={() => setOpen(isOpen ? null : s.id)}
                  aria-expanded={isOpen}
                  initial={{ opacity: 0, y: 18 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.3 }}
                  transition={{ duration: 0.75, ease: [0.7, 0, 0.3, 1], delay: i * 0.05 }}
                  className="relative w-full text-left grid grid-cols-12 gap-6 items-center py-8 md:py-10 transition-colors duration-500 hover:bg-[var(--nv-paper)]/[0.03]"
                  data-testid={`service-toggle-${s.id}`}
                >
                  {/* Left→right gold progress line on hover / open */}
                  <span
                    aria-hidden
                    className={`pointer-events-none absolute left-0 bottom-0 h-px bg-[var(--nv-gold)] origin-left transition-transform duration-[900ms] ease-[cubic-bezier(0.7,0,0.3,1)] ${
                      isOpen ? "scale-x-100" : "scale-x-0 group-hover:scale-x-100"
                    }`}
                    style={{ width: "100%" }}
                  />

                  <span className="col-span-2 md:col-span-1 font-mono text-xs tracking-[0.24em] text-[var(--nv-gold)]">
                    {s.tag}
                  </span>
                  <div className="col-span-10 md:col-span-6 flex items-center gap-4">
                    <span className="inline-flex items-center justify-center w-11 h-11 rounded-full border border-[var(--nv-paper)]/25 group-hover:border-[var(--nv-gold)] group-hover:shadow-[0_0_0_6px_rgba(197,160,89,0.08)] transition-all duration-500">
                      <Icon
                        className="w-4 h-4 transition-transform duration-500 group-hover:scale-110 group-hover:text-[var(--nv-gold)]"
                        strokeWidth={1.4}
                      />
                    </span>
                    <h3 className="font-serif text-3xl md:text-5xl tracking-tight leading-none transition-colors duration-500 group-hover:text-[var(--nv-gold-soft)]">
                      {s.title}
                    </h3>
                  </div>
                  <p className="hidden md:block col-span-4 text-sm leading-relaxed text-[var(--nv-paper)]/70">
                    {s.body}
                  </p>
                  <span className="col-span-2 md:col-span-1 justify-self-end flex items-center gap-3 text-[var(--nv-paper)]/60 group-hover:text-[var(--nv-gold)] transition-colors duration-500">
                    <span className="hidden md:inline font-mono text-[10px] tracking-[0.24em] uppercase">
                      {isOpen ? "Close" : "Detail"}
                    </span>
                    <span className="relative inline-flex items-center justify-center w-9 h-9 rounded-full border border-[var(--nv-paper)]/25 group-hover:border-[var(--nv-gold)] transition-all duration-500 overflow-hidden">
                      <AnimatePresence mode="wait" initial={false}>
                        {isOpen ? (
                          <motion.span
                            key="close"
                            initial={{ rotate: -45, opacity: 0 }}
                            animate={{ rotate: 0, opacity: 1 }}
                            exit={{ rotate: 45, opacity: 0 }}
                            transition={{ duration: 0.25 }}
                          >
                            <Minus className="w-4 h-4" strokeWidth={1.6} />
                          </motion.span>
                        ) : (
                          <motion.span
                            key="open"
                            initial={{ rotate: -45, opacity: 0 }}
                            animate={{ rotate: 0, opacity: 1 }}
                            exit={{ rotate: 45, opacity: 0 }}
                            transition={{ duration: 0.25 }}
                          >
                            <Plus className="w-4 h-4" strokeWidth={1.6} />
                          </motion.span>
                        )}
                      </AnimatePresence>
                    </span>
                  </span>
                </motion.button>

                {/* Expandable detail */}
                <AnimatePresence initial={false}>
                  {isOpen && (
                    <motion.div
                      key="detail"
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.5, ease: [0.7, 0, 0.3, 1] }}
                      className="overflow-hidden"
                      data-testid={`service-detail-${s.id}`}
                    >
                      <div className="grid grid-cols-12 gap-6 md:gap-10 pb-10 md:pb-14 md:pl-[calc(8.333%_+_1.5rem)]">
                        <DetailBlock
                          label="Key Deliverables"
                          items={s.deliverables}
                        />
                        <DetailBlock
                          label="Typical Scope"
                          items={[s.scope]}
                          prose
                        />
                        <DetailBlock
                          label="Areas of Expertise"
                          items={s.expertise}
                        />
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </li>
            );
          })}
        </ul>

        {/* Section closing statement */}
        <motion.div
          initial={{ opacity: 0, y: 18 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.4 }}
          transition={{ duration: 0.8, ease: [0.7, 0, 0.3, 1] }}
          className="mt-16 md:mt-24 grid grid-cols-12 gap-6 items-end"
          data-testid="services-closing"
        >
          <div className="col-span-12 md:col-span-8">
            <div className="overline text-[var(--nv-gold)] flex items-center gap-3">
              <span className="inline-block w-6 h-px bg-[var(--nv-gold)]" />
              <span>In sum</span>
            </div>
            <p className="mt-6 font-serif text-2xl md:text-3xl lg:text-4xl leading-[1.25] tracking-tight text-[var(--nv-paper)]">
              Every service can stand alone —{" "}
              <span className="italic text-[var(--nv-gold)]">
                or work together as one seamless business solution.
              </span>
            </p>
          </div>
          <div className="col-span-12 md:col-span-4 md:justify-self-end">
            <a href="#contact" className="btn-ghost on-dark" data-testid="services-cta">
              Discuss Your Requirement <ArrowUpRight className="w-3.5 h-3.5" />
            </a>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

function DetailBlock({ label, items, prose = false }) {
  return (
    <div className="col-span-12 md:col-span-4">
      <div className="overline text-[var(--nv-gold)] mb-4">{label}</div>
      {prose ? (
        <p className="text-sm md:text-[15px] leading-relaxed text-[var(--nv-paper)]/75">{items[0]}</p>
      ) : (
        <ul className="space-y-2.5">
          {items.map((it) => (
            <li key={it} className="flex items-start gap-3 text-sm text-[var(--nv-paper)]/80">
              <span className="mt-2 inline-block w-1.5 h-1.5 rounded-full bg-[var(--nv-gold)] shrink-0" />
              <span className="leading-relaxed">{it}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function ServicesBlueprintLayer() {
  return (
    <div className="absolute inset-0 pointer-events-none" aria-hidden>
      <svg
        viewBox="0 0 1600 2000"
        preserveAspectRatio="xMidYMid slice"
        className="absolute inset-0 w-full h-full opacity-[0.09]"
      >
        <defs>
          <pattern id="svc-grid" x="0" y="0" width="80" height="80" patternUnits="userSpaceOnUse">
            <path d="M 80 0 L 0 0 0 80" fill="none" stroke="#FAFAFA" strokeWidth="0.5" />
          </pattern>
        </defs>
        <rect width="100%" height="100%" fill="url(#svc-grid)" />

        <g stroke="#FAFAFA" strokeWidth="0.7" fill="none">
          {/* Top-right compass ring */}
          <circle cx="1400" cy="220" r="180" strokeDasharray="2 6" />
          <circle cx="1400" cy="220" r="110" />
          <circle cx="1400" cy="220" r="3" fill="#C5A059" stroke="none" />
          <line x1="1400" y1="40" x2="1400" y2="400" strokeDasharray="1 4" />
          <line x1="1220" y1="220" x2="1580" y2="220" strokeDasharray="1 4" />

          {/* Middle-left node */}
          <circle cx="120" cy="1000" r="90" strokeDasharray="2 6" />
          <circle cx="120" cy="1000" r="3" fill="#C5A059" stroke="none" />

          {/* Bottom-right axonometric line */}
          <path d="M 1240 1720 L 1440 1620 L 1600 1720 L 1600 1900 L 1440 2000" />
          <path d="M 1440 1620 L 1440 1800 L 1600 1900" />
        </g>

        {/* Gold accent points */}
        <g fill="#C5A059">
          <circle cx="360" cy="180" r="2.5" />
          <circle cx="1080" cy="820" r="2.5" />
          <circle cx="900" cy="1520" r="2.5" />
          <circle cx="60" cy="500" r="2.5" />
        </g>

        {/* Mono annotations */}
        <g fontFamily="'JetBrains Mono', ui-monospace, monospace" fill="#FAFAFA" fontSize="10" letterSpacing="0.24em">
          <text x="1400" y="30">NV — 03</text>
          <text x="60" y="80">SVC · GRID</text>
          <text x="1220" y="1600">FIG · C</text>
        </g>
      </svg>
    </div>
  );
}
